import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

function MarkdownRenderer({
    content,
    className = '',
    math = true,
    gfm = false,
}: {
    content: string;
    className?: string;
    math?: boolean;
    gfm?: boolean;
}) {
    const remarkPlugins = [
        ...(gfm ? [remarkGfm] : []),
        ...(math ? [remarkMath] : []),
    ];

    return (
        <div className={`markdown-content ${className}`}>
            <ReactMarkdown
                // math=false for PROSE content (e.g. planner node descriptions):
                // a pair of '$' amounts in ordinary text would otherwise be
                // swallowed as inline TeX and render garbled.
                remarkPlugins={remarkPlugins}
                rehypePlugins={math ? [rehypeKatex] : []}
                components={{
                    // Custom table styling
                    table: ({ children }) => (
                        <table className="border-collapse border border-border-default my-2 text-xs">
                            {children}
                        </table>
                    ),
                    th: ({ children }) => (
                        <th className="border border-border-default px-2 py-1 bg-bg-warm font-semibold text-left">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="border border-border-default px-2 py-1">
                            {children}
                        </td>
                    ),
                    li: ({ children, className }) => (
                        className?.includes('task-list-item') ? (
                            <li className={className}>
                                <label className="block">
                                    {children}
                                </label>
                            </li>
                        ) : (
                            <li className={className}>{children}</li>
                        )
                    ),
                    input: ({ node, className, ...props }) => {
                        void node;
                        return (
                            <input
                                {...props}
                                className={`mr-1.5 align-middle ${className ?? ''}`}
                            />
                        );
                    },
                    // Custom heading styling
                    h1: ({ children }) => <h1 className="text-sm font-bold mt-3 mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-sm font-semibold mt-3 mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-xs font-semibold mt-2 mb-1">{children}</h3>,
                    // Custom paragraph styling
                    p: ({ children }) => <p className="text-xs leading-relaxed mb-1">{children}</p>,
                    // Custom code styling for equations
                    code: ({ children }) => <code className="text-xs bg-bg-warm px-1 rounded">{children}</code>,
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}

export default MarkdownRenderer;
